/**
 * Position File Service - Handles position-based file uploads
 * Connects to backend API for file operations per order position
 */

import { PositionFileResponse } from '../types/unifiedOrderTypes';

// API Base URL - configurable for different environments
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

class PositionFileService {
  private baseUrl: string = API_BASE_URL;

  /**
   * Upload files for a specific position
   */
  async uploadPositionFiles(
    orderId: string,
    positionId: string,
    files: File[],
    description?: string
  ): Promise<PositionFileResponse> {
    try {
      const formData = new FormData();
      
      files.forEach(file => {
        formData.append('files', file);
      });
      
      if (description) {
        formData.append('description', description);
      }

      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload files');
      }

      return data;
    } catch (error: any) {
      console.error('Error uploading position files:', error);
      return {
        success: false,
        message: error.message || 'Failed to upload files'
      };
    }
  }

  /**
   * Get all files for a specific position
   */
  async getPositionFiles(
    orderId: string,
    positionId: string
  ): Promise<PositionFileResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch position files');
      }

      return data;
    } catch (error: any) {
      console.error('Error fetching position files:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch position files'
      };
    }
  }

  /**
   * Get all position files for an order
   */
  async getOrderPositionFiles(orderId: string): Promise<PositionFileResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/files/positions`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch order position files');
      }

      return data;
    } catch (error: any) {
      console.error('Error fetching order position files:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch order position files'
      };
    }
  }

  /**
   * Download a specific position file
   */
  async downloadPositionFile(
    orderId: string,
    positionId: string,
    fileId: string,
    filename: string
  ): Promise<void> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files/${fileId}/download`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download file');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading position file:', error);
      throw new Error(error.message || 'Failed to download file');
    }
  }

  /**
   * Update position file description
   */
  async updatePositionFileDescription(
    orderId: string,
    positionId: string,
    fileId: string,
    description: string
  ): Promise<PositionFileResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files/${fileId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ description }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update file description');
      }

      return data;
    } catch (error: any) {
      console.error('Error updating position file description:', error);
      return {
        success: false,
        message: error.message || 'Failed to update file description'
      };
    }
  }

  /**
   * Delete a position file
   */
  async deletePositionFile(
    orderId: string,
    positionId: string,
    fileId: string
  ): Promise<PositionFileResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/${orderId}/positions/${positionId}/files/${fileId}`,
        {
          method: 'DELETE',
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete file');
      }

      return data;
    } catch (error: any) {
      console.error('Error deleting position file:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete file'
      };
    }
  }
}

export const positionFileService = new PositionFileService();
export default positionFileService;
